import { Text, TextLink } from '@audio-network/componentlibrary'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { Fragment } from 'react'

import { getComposerUrl } from '@/utils/urls'

import DescriptionDetails from '../DescriptionList/DescriptionDetails/DescriptionDetails'
import DescriptionList from '../DescriptionList/DescriptionList'
import DescriptionTerm from '../DescriptionList/DescriptionTerm/DescriptionTerm'
import CopyButton from './CopyButton/CopyButton'
import styles from './TrackCueSheet.module.css'
import { TrackCueSheetProps } from './TrackCueSheet.types'
import {
	buildComposersTable,
	buildPublishersTable,
	handleArtistClicked,
	setLabelCode,
} from './TrackCueSheet.utils'

const TrackCueSheet = ({
	track,
	className,
}: TrackCueSheetProps): JSX.Element => {
	const { t } = useTranslation(['app'])
	return (
		<DescriptionList className={clsx(styles.list, className)}>
			<DescriptionTerm>{t('composers')}</DescriptionTerm>
			<DescriptionDetails>
				{track.composers.map((composer) => {
					const { id, name, collectionSociety, ipi, split } = composer
					const showRoyaltySplit = collectionSociety && ipi && split

					const link = getComposerUrl(composer)

					return (
						<Fragment key={id}>
							<TextLink href={link} onClick={() => handleArtistClicked(name)}>
								{name}
							</TextLink>

							{showRoyaltySplit && (
								<Text color="ui2" variant="inherit">
									{`${collectionSociety}: ${ipi}, ${split}%`}
								</Text>
							)}
						</Fragment>
					)
				})}
				<CopyButton
					data-testid="composers-copy-button"
					table={buildComposersTable(track.composers)}
					buttonType="copy"
				/>
			</DescriptionDetails>

			{track.publishers?.length > 0 && (
				<>
					<DescriptionTerm>{t('publisher')}</DescriptionTerm>
					<DescriptionDetails>
						{track.publishers.map((publisher) => {
							const { id, name, collectionSociety, ipi, split } = publisher
							const showRoyaltySplit = collectionSociety && ipi && split

							return (
								<Fragment key={id}>
									<Text variant="inherit">{name}</Text>
									{showRoyaltySplit && (
										<Text color="ui2" variant="inherit">
											{`${collectionSociety}: ${ipi}, ${split}%`}
										</Text>
									)}
								</Fragment>
							)
						})}
						<CopyButton
							data-testid="publishers-copy-button"
							table={buildPublishersTable(track.publishers)}
							buttonType="copy"
						/>
					</DescriptionDetails>
				</>
			)}

			{track.iswc && (
				<>
					<DescriptionTerm>{t('iswc')}</DescriptionTerm>
					<DescriptionDetails>
						{track.iswc}
						<CopyButton
							data-testid="iswc-copy-button"
							table={track.iswc}
							buttonType="copy"
						/>
					</DescriptionDetails>
				</>
			)}
			{track.isrc && (
				<>
					<DescriptionTerm>{t('isrc')}</DescriptionTerm>
					<DescriptionDetails>
						{track.isrc}
						<CopyButton
							data-testid="isrc-copy-button"
							table={track.isrc}
							buttonType="copy"
						/>
					</DescriptionDetails>
				</>
			)}
			{track.publishers?.length > 0 && (
				<>
					<DescriptionTerm>{t('label-code')}</DescriptionTerm>
					<DescriptionDetails>
						{track.publishers
							.map((publisher) => setLabelCode(publisher))
							.join(', ')}
						<CopyButton
							table={track.publishers
								.map((publisher) => setLabelCode(publisher))
								.join(', ')}
							buttonType="copy"
						/>
					</DescriptionDetails>
				</>
			)}
			<DescriptionTerm>{t('release-year')}</DescriptionTerm>
			<DescriptionDetails>
				{new Date(track.releaseDate).getFullYear().toString()}
				<CopyButton
					data-testid="release-year-copy-button"
					table={new Date(track.releaseDate).getFullYear().toString()}
					buttonType="copy"
				/>
			</DescriptionDetails>
		</DescriptionList>
	)
}

export default TrackCueSheet
